<script setup lang="ts">
import VitaBeruCard from "@/components/VitaBeruCard.vue";
import VitaBeruCreateModal from "@/components/VitaBeruCreateModal.vue";
import VitaBeruDetailModal from "@/components/VitaBeruDetailModal.vue";
import VitaEmptyPlaceholder from "@/components/UI/VitaEmptyPlaceholder.vue";
import { onMounted, ref } from "vue";
import {
  PerfectScrollbar,
  PerfectScrollbarExpose,
} from "vue3-perfect-scrollbar";
import VitaButton from "./UI/VitaButton.vue";
import { BeruCardFree, BeruCardTaken } from "@/types/beru";
import { useBeruExchange } from "@/composables/useBeruExchange";
import VitaLoaderSpinner from "./UI/VitaLoaderSpinner.vue";
import { reachGoal } from "@/utils/metrika";

const isModalOpen = ref(false);
const isCreateModalOpen = ref(false);
const selectedCard = ref<BeruCardFree | BeruCardTaken | null>(null);
const freeScrollRef = ref<PerfectScrollbarExpose | null>(null);
const takenScrollRef = ref<PerfectScrollbarExpose | null>(null);

const scrollOptions = {
  minScrollbarLength: 60,
  wheelPropagation: false,
  suppressScrollX: true,
};

const openModal = (item: BeruCardFree | BeruCardTaken) => {
  selectedCard.value = item;
  isModalOpen.value = true;
};

// Свободные смены по Беру
const {
  error: hasExchangeFreeError,
  isLoading: isExchangeFreeLoading,
  showLoaderMore: showLoaderMoreFree,
  hasLoadMoreError: hasLoadMoreFreeError,
  data: exchangeFree,
  fetchInitial: getBeruExchangeFree,
} = useBeruExchange(
  "beru/getFreeBeru",
  "beru/exchangeFree",
  "beru/exchangeFreeMeta",
  () => freeScrollRef.value?.ps?.element ?? null,
);

// Взятые смены по Беру
const {
  error: hasExchangeTakenError,
  isLoading: isExchangeTakenLoading,
  showLoaderMore: showLoaderMoreTaken,
  hasLoadMoreError: hasLoadMoreTakenError,
  data: exchangeTaken,
  fetchInitial: getBeruExchangeTaken,
} = useBeruExchange(
  "beru/getTakenBeru",
  "beru/exchangeTaken",
  "beru/exchangeTakenMeta",
  () => takenScrollRef.value?.ps?.element ?? null,
);

const getAllBeruExchange = () => {
  getBeruExchangeFree();
  getBeruExchangeTaken();
}

const onCreateBeruClick = () => {
  isCreateModalOpen.value = true
  reachGoal('beru-create-click')
}

onMounted(() => {
  getAllBeruExchange();
});
</script>

<template>
  <div class="list-wrapper">
    <div class="list-header">
      <div>Свободные</div>
      <div>Взятые</div>
    </div>
    <div class="list-columns">
      <div>
        <template
          v-if="
            (isExchangeFreeLoading && !hasExchangeFreeError) ||
            showLoaderMoreFree
          "
        >
          <VitaLoaderSpinner />
        </template>
        <PerfectScrollbar
          ref="freeScrollRef"
          class="list-columns-scroll rail-beru rail-thin rail-lightgray"
          :options="scrollOptions"
          v-else-if="
            !hasLoadMoreFreeError &&
            !isExchangeFreeLoading &&
            exchangeFree &&
            exchangeFree.length > 0
          "
        >
          <div class="list-columns-grid has-button">
            <vita-beru-card
              v-for="(item, idx) in exchangeFree"
              :key="idx"
              :card="item"
              @click="openModal(item)"
              data-qa="beru-layout-card-free-open"
            />
          </div>
        </PerfectScrollbar>
        <template v-else-if="hasLoadMoreFreeError || hasExchangeFreeError">
          <div class="empty-placeholder">
            <VitaEmptyPlaceholder
              qa-name="beru-free-error"
              icon="isOffline"
              :message="hasExchangeFreeError"
              description="При загрузке данных произошла ошибка. Попробуйте обновить страницу позже."
              icon-width="100"
              icon-height="100"
              :on-refresh-click="getBeruExchangeFree"
            />
          </div>
        </template>
        <template v-else-if="exchangeFree && exchangeFree.length === 0">
          <div class="empty-placeholder">
            <VitaEmptyPlaceholder
              qa-name="beru-free-empty"
              icon="isEmptyWindow"
              message="Пока нет ни одной свободной&nbsp;потребности"
              description="Создайте новую потребность"
              icon-width="100"
              icon-height="100"
            />
          </div>
        </template>
      </div>
      <div>
        <template
          v-if="
            (isExchangeTakenLoading && !hasExchangeTakenError) ||
            showLoaderMoreTaken
          "
        >
          <VitaLoaderSpinner />
        </template>
        <PerfectScrollbar
          ref="takenScrollRef"
          class="list-columns-scroll rail-beru rail-thin rail-lightgray"
          :options="scrollOptions"
          v-else-if="
            !hasLoadMoreTakenError &&
            !isExchangeTakenLoading &&
            exchangeTaken &&
            exchangeTaken.length > 0
          "
        >
          <div class="list-columns-grid">
            <vita-beru-card
              v-for="(item, idx) in exchangeTaken"
              :key="idx"
              :card="item"
              @click="openModal(item)"
              data-qa="beru-layout-card-taken-open"
            />
          </div>
        </PerfectScrollbar>
        <template v-else-if="hasExchangeTakenError || hasLoadMoreTakenError">
          <div class="empty-placeholder">
            <VitaEmptyPlaceholder
              qa-name="beru-taken-error"
              icon="isOffline"
              :message="hasExchangeTakenError"
              description="При загрузке данных произошла ошибка. Попробуйте обновить страницу позже."
              icon-width="100"
              icon-height="100"
              :on-refresh-click="getBeruExchangeTaken"
            />
          </div>
        </template>
        <template v-else-if="exchangeTaken && exchangeTaken.length === 0">
          <div class="empty-placeholder">
            <VitaEmptyPlaceholder
              qa-name="beru-taken-empty"
              icon="isEmptyBox"
              message="Пока нет ни одной взятой&nbsp;потребности"
              description="Создайте новую потребность"
              icon-width="100"
              icon-height="100"
            />
          </div>
        </template>
      </div>
    </div>
    <vita-button
      class="list-btn-floating rounded no-shadow"
      color="blue"
      width="inline"
      @click="onCreateBeruClick"
      data-qa="beru-layout-button-create"
    >
      Создать потребность Беру!
    </vita-button>
  </div>
  <vita-beru-detail-modal
    :open="isModalOpen"
    :card="selectedCard"
    @canceled="getAllBeruExchange"
    @toggleInfoModal="isModalOpen = false"
  />

  <vita-beru-create-modal
    :open="isCreateModalOpen"
    @toggleInfoModal="isCreateModalOpen = false"
    @created="getBeruExchangeFree"
  />
</template>

<style scoped lang="scss">
.list-wrapper {
  border: 1px solid rgba(204, 204, 204, 1);
  padding-top: 16px;
  border-radius: 16px;
  position: relative;
  margin-right: 25px;
  min-height: calc(100% - 3px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.list-header {
  background-color: rgba(245, 251, 255, 1);
  color: rgba(25, 88, 134, 1);
  padding: 10px;
  font-weight: 600;
  font-size: 15px;
  line-height: 100%;
  letter-spacing: 0;
  text-align: center;
  display: flex;
  align-items: center;
  position: relative;
  border-radius: 16px;
  margin-right: 16px;
  margin-left: 16px;

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 0.5px;
    height: 14px;
    background-color: rgba(25, 88, 134, 1);
  }

  & > div {
    flex: 1;
  }
}

.list-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 4px;
  align-items: start;
  position: relative;
  min-height: 100%;
  flex: 1;

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 0.5px;
    height: calc(100% - 32px);
    background-color: rgba(187, 187, 187, 1);
  }

  & > div {
    height: calc(100vh - 119px);
    position: relative;
  }
}

.list-columns-scroll {
  height: 100%;
  padding-left: 16px;
  padding-right: 15px;
  padding-top: 16px;
  overflow-y: hidden;
}

.list-columns-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(310px, 1fr));
  gap: 8px;
  padding-bottom: 20px;

  &.has-button {
    padding-bottom: 60px;
  }

  &:has(.vue-skeletor) {
    padding: 16px;
  }
}

.empty-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 24px;
  height: 100%;

  &-title {
    font-size: 15px;
    font-weight: 500;
    line-height: 1.2;
    letter-spacing: 0;
  }

  &-description {
    font-size: 13px;
    font-weight: 500;
    line-height: 1.25;
    letter-spacing: 0;
    color: rgba(136, 136, 136, 1);

    &:not(:first-child) {
      margin-top: 8px;
    }
  }
}

.list-btn-floating {
  position: absolute;
  bottom: 16px;
  left: 16px;
  z-index: 1;
  min-width: 332px;
}

:deep(.loader-alt) {
  margin-top: -45px;
  position: absolute;
  inset: 0;
  height: auto;
}

:deep(.placeholder-message) {
  max-width: 250px;
}

.load-more-loader {
  padding: 12px 16px 20px;
}

.load-more-error {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 12px 16px 20px;
  font-size: 13px;
  font-weight: 500;
  color: rgba(148, 156, 175, 1);
  transform: translateY(-44px);
}

.load-more-retry {
  font-family: "Montserrat", sans-serif;
  background: transparent;
  border: none;
  padding: 0;
  appearance: none;
  font-size: 13px;
  font-weight: 500;
  color: #30a8ff;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.8;
  }
}
</style>
